/**
 * Turnstile Verification Page
 *
 * This page hosts the Cloudflare Turnstile widget for mobile app verification.
 * It's loaded in a WebView from the React Native app.
 *
 * Query Parameters:
 * - phone: The phone number to display (optional, for UX)
 *
 * Communication:
 * - On success: postMessage({ type: 'success', token: '...' })
 * - On error: postMessage({ type: 'error', error: '...' })
 * - On expired: postMessage({ type: 'expired' })
 */

import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { TURNSTILE_SITE_KEY } from '../utils/env'

type VerificationState = 'loading' | 'verifying' | 'success' | 'error'

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string
          theme?: 'light' | 'dark' | 'auto'
          callback?: (token: string) => void
          'error-callback'?: (error: string) => void
          'expired-callback'?: () => void
        },
      ) => string
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
    }
    ReactNativeWebView?: {
      postMessage: (message: string) => void
    }
  }
}

function VerifyPage() {
  const [searchParams] = useSearchParams()
  const phone = searchParams.get('phone') || ''
  const [state, setState] = useState<VerificationState>('loading')
  const [widgetId, setWidgetId] = useState<string | null>(null)

  // Format phone number for display
  const formatPhoneDisplay = (phoneNumber: string) => {
    if (phoneNumber.length > 6) {
      const countryCode = phoneNumber.slice(0, phoneNumber.length - 10)
      const areaCode = phoneNumber.slice(-10, -7)
      const firstPart = phoneNumber.slice(-7, -4)
      const lastPart = phoneNumber.slice(-4)
      return `${countryCode} ${areaCode}-${firstPart}-${lastPart}`
    }
    return phoneNumber
  }

  // Send message to React Native WebView
  const postToApp = (message: { type: string; token?: string; error?: string }) => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify(message))
    }
    // For debugging in browser (silent in production)
  }

  // Initialize Turnstile widget
  useEffect(() => {
    const initTurnstile = () => {
      if (!window.turnstile) {
        // Script not loaded yet, retry
        setTimeout(initTurnstile, 100)
        return
      }

      if (!TURNSTILE_SITE_KEY) {
        setState('error')
        postToApp({ type: 'error', error: 'Turnstile not configured' })
        return
      }

      setState('verifying')

      const id = window.turnstile.render('#turnstile-container', {
        sitekey: TURNSTILE_SITE_KEY,
        theme: 'light',
        callback: (token: string) => {
          setState('success')
          postToApp({ type: 'success', token })
        },
        'error-callback': (error: string) => {
          setState('error')
          postToApp({ type: 'error', error: error || 'Verification failed' })
        },
        'expired-callback': () => {
          setState('verifying')
          postToApp({ type: 'expired' })
        },
      })

      setWidgetId(id)
    }

    // Load Turnstile script if not already loaded
    if (!document.querySelector('script[src*="turnstile"]')) {
      const script = document.createElement('script')
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
      script.async = true
      script.defer = true
      script.onload = initTurnstile
      document.head.appendChild(script)
    } else {
      initTurnstile()
    }

    return () => {
      if (widgetId && window.turnstile) {
        window.turnstile.remove(widgetId)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRetry = () => {
    if (widgetId && window.turnstile) {
      window.turnstile.reset(widgetId)
      setState('verifying')
    }
  }

  const handleCancel = () => {
    postToApp({ type: 'error', error: 'cancelled' })
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Handle indicator */}
        <div style={styles.handleContainer}>
          <div style={styles.handle} />
        </div>

        {/* Header */}
        <h1 style={styles.title}>Get your code</h1>
        <p style={styles.subtitle}>
          We&apos;ll send a verification code to
          <br />
          <span style={styles.phoneNumber}>{formatPhoneDisplay(phone)}</span>
        </p>

        {/* Turnstile Widget Container */}
        <div style={styles.turnstileContainer}>
          {state === 'loading' && (
            <div style={styles.loadingContainer}>
              <div style={styles.spinner} />
              <span style={styles.loadingText}>Loading verification...</span>
            </div>
          )}

          {state === 'success' && (
            <div style={styles.successContainer}>
              <div style={styles.successIcon}>✓</div>
              <span style={styles.successText}>Success!</span>
              <span style={styles.cloudflareText}>CLOUDFLARE</span>
            </div>
          )}

          {state === 'error' && (
            <div style={styles.errorContainer}>
              <div style={styles.errorIcon}>✕</div>
              <span style={styles.errorText}>Verification failed</span>
              <button style={styles.retryButton} onClick={handleRetry}>
                Try Again
              </button>
            </div>
          )}

          {/* Turnstile widget renders here */}
          <div
            id="turnstile-container"
            style={{
              ...styles.widgetContainer,
              display: state === 'verifying' ? 'flex' : 'none',
            }}
          />
        </div>

        {/* Status message */}
        {state === 'success' && <p style={styles.sendingText}>Sending verification code...</p>}

        {/* Cancel button */}
        <button style={styles.cancelButton} onClick={handleCancel}>
          Cancel
        </button>
      </div>
    </div>
  )
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  },
  card: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: '0 24px 40px',
    width: '100%',
    maxWidth: 500,
    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)',
  },
  handleContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '12px 0',
  },
  handle: {
    width: 40,
    height: 4,
    backgroundColor: '#ddd',
    borderRadius: 2,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#000',
    textAlign: 'center',
    marginTop: 8,
    marginBottom: 0,
  },
  subtitle: {
    fontSize: 15,
    color: '#666',
    textAlign: 'center',
    marginTop: 8,
    lineHeight: 1.5,
  },
  phoneNumber: {
    color: '#000',
    fontWeight: 600,
  },
  turnstileContainer: {
    marginTop: 24,
    minHeight: 80,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    padding: '16px 20px',
    borderRadius: 12,
    border: '1px solid #ddd',
    gap: 12,
  },
  spinner: {
    width: 20,
    height: 20,
    border: '2px solid #ddd',
    borderTopColor: '#3B82F6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    color: '#666',
    fontSize: 14,
  },
  widgetContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  successContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f0fdf4',
    padding: '16px 20px',
    borderRadius: 12,
    border: '1px solid #bbf7d0',
    gap: 12,
    width: '100%',
  },
  successIcon: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#22c55e',
    color: '#fff',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: 16,
    fontWeight: 'bold',
  },
  successText: {
    color: '#166534',
    fontSize: 16,
    fontWeight: 600,
    flex: 1,
  },
  cloudflareText: {
    color: '#f48120',
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 0.5,
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
  },
  errorIcon: {
    fontSize: 32,
    color: '#ef4444',
  },
  errorText: {
    color: '#ef4444',
    fontSize: 16,
    fontWeight: 500,
  },
  retryButton: {
    backgroundColor: '#3B82F6',
    color: '#fff',
    padding: '12px 24px',
    borderRadius: 25,
    border: 'none',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
  },
  sendingText: {
    textAlign: 'center',
    color: '#22c55e',
    fontSize: 14,
    marginTop: 16,
    fontWeight: 500,
  },
  cancelButton: {
    marginTop: 24,
    padding: 16,
    backgroundColor: '#f5f5f5',
    borderRadius: 25,
    border: '1px solid #ddd',
    width: '100%',
    fontSize: 16,
    fontWeight: 500,
    color: '#333',
    cursor: 'pointer',
  },
}

// Add keyframes for spinner animation
const styleSheet = document.createElement('style')
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`
document.head.appendChild(styleSheet)

export default VerifyPage
